import { ORDER_SUBMIT_IN_PROGRESS_ERROR } from '@supply-mover-2-0/shared';

/**
 * PD-16731 — what checkout does when Place Order does not come back with a
 * clear answer.
 *
 * The load balancer answers 504 after 60 seconds while the server goes on to
 * place the order, so "Request failed with status 504" used to be shown for
 * orders that were placed. A submit whose outcome is unknown now asks whether
 * the cart became an order before saying anything.
 */

/**
 * `unknown` — the order may have been placed: no response arrived (a dropped
 * connection), a gateway answered for the server (502, 503, 504), or another
 * submit for this account was still running (the submit-lock 409).
 * `refused` — the server answered and said no.
 */
export type StorefrontSubmitFailureKindModel = 'unknown' | 'refused';

/**
 * `placed` — the cart became an order. `notPlaced` — it is still a cart and
 * no submit is running. `unconfirmed` — the status could not be read before
 * the wait ran out.
 */
export type StorefrontPlacementModel = 'placed' | 'notPlaced' | 'unconfirmed';

/** How often the submit status is re-read while a submit is still running. */
export const STOREFRONT_SUBMIT_STATUS_POLL_INTERVAL_MS = 3_000;

/**
 * The longest the status is polled: the storefront submit lock's lifetime on
 * the server, after which no submit can still be running.
 */
export const STOREFRONT_SUBMIT_STATUS_MAX_WAIT_MS = 120_000;

const GATEWAY_STATUSES = new Set<number>([502, 503, 504]);

/** The HTTP status the portal API client attaches to the errors it throws. */
function statusOf(error: unknown): number | null {
	if (typeof error !== 'object' || error == null || !('status' in error)) {
		return null;
	}

	const status = (error as { status: unknown }).status;

	return typeof status === 'number' ? status : null;
}

function errorCodeOf(error: unknown): unknown {
	if (typeof error !== 'object' || error == null || !('data' in error)) {
		return null;
	}

	const data = (error as { data: unknown }).data;

	return typeof data === 'object' && data != null
		? (data as { error?: unknown }).error
		: null;
}

/** Whether a failed Place Order may still have been placed on the server. */
export function classifyStorefrontSubmitFailure(
	error: unknown,
): StorefrontSubmitFailureKindModel {
	const status = statusOf(error);

	if (status == null) {
		// No HTTP response (fetch rejects with a TypeError when the connection
		// drops), or a success response that could not be read.
		return 'unknown';
	}

	if (GATEWAY_STATUSES.has(status)) {
		return 'unknown';
	}

	if (status === 409 && errorCodeOf(error) === ORDER_SUBMIT_IN_PROGRESS_ERROR) {
		return 'unknown';
	}

	return 'refused';
}

/**
 * Read the cart's submit status until it gives an answer: every
 * `pollIntervalMs` while a submit is still running, up to `maxWaitMs`. A read
 * that fails without an answer (a dropped connection, a gateway error) is
 * retried within the same window; one the server refuses (403, 404) will not
 * change by waiting and ends the wait at once.
 */
export async function confirmStorefrontPlacement(params: {
	fetchStatus: () => Promise<{ isCart: boolean; submitInProgress: boolean }>;
	pollIntervalMs?: number;
	maxWaitMs?: number;
	sleep?: (ms: number) => Promise<void>;
	now?: () => number;
}): Promise<StorefrontPlacementModel> {
	const pollIntervalMs =
		params.pollIntervalMs ?? STOREFRONT_SUBMIT_STATUS_POLL_INTERVAL_MS;
	const maxWaitMs = params.maxWaitMs ?? STOREFRONT_SUBMIT_STATUS_MAX_WAIT_MS;
	const sleep =
		params.sleep ??
		((ms: number): Promise<void> =>
			new Promise((resolve) => {
				setTimeout(resolve, ms);
			}));
	const now = params.now ?? ((): number => Date.now());
	const deadline = now() + maxWaitMs;

	for (;;) {
		try {
			const status = await params.fetchStatus();

			if (!status.isCart) {
				return 'placed';
			}

			if (!status.submitInProgress) {
				return 'notPlaced';
			}
		} catch (error: unknown) {
			const status = statusOf(error);

			if (status != null && status >= 400 && status < 500) {
				return 'unconfirmed';
			}
		}

		if (now() + pollIntervalMs > deadline) {
			return 'unconfirmed';
		}

		await sleep(pollIntervalMs);
	}
}
